<template>
  <div v-if="open" class="num-pad">
    <div class="num-pad__backdrop" @click="done"></div>
    <div class="num-pad__panel" @mousedown.prevent @touchstart.stop>
      <div class="num-pad__top">
        <strong>{{ title || $t('common.touchPad') }}</strong>
        <button type="button" class="num-pad__done" @click="done">
          {{ $t('cash.searchKeyboard.done') }}
        </button>
      </div>

      <div class="num-pad__value">
        <span :class="{ 'is-placeholder': !displayValue }">{{ displayValue || placeholder }}</span>
        <button
          v-if="value !== '' && value != null"
          type="button"
          class="num-pad__clear"
          @click="clear"
        >
          <i class="mdi mdi-close-circle"></i>
        </button>
      </div>

      <div class="num-pad__grid" :class="{ 'num-pad__grid--decimal': allowDecimal && mode === 'number' }">
        <button
          v-for="(key, keyIndex) in keys"
          :key="`num-pad-${keyIndex}`"
          type="button"
          class="num-pad__key"
          :class="{
            'num-pad__key--action': key === 'back' || key === 'ok',
            'num-pad__key--ok': key === 'ok',
            'num-pad__key--ghost': key === '',
            'num-pad__key--wide': key === '0' && !(allowDecimal && mode === 'number'),
          }"
          :disabled="key === ''"
          @click="press(key)"
        >
          <i v-if="key === 'back'" class="mdi mdi-backspace-outline"></i>
          <template v-else-if="key === 'ok'">OK</template>
          <template v-else>{{ key }}</template>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NumericPad',
  props: {
    open: { type: Boolean, default: false },
    value: { type: [String, Number], default: '' },
    title: { type: String, default: '' },
    placeholder: { type: String, default: '0' },
    maxLength: { type: Number, default: 12 },
    mode: { type: String, default: 'number' }, // number | phone
    allowDecimal: { type: Boolean, default: false },
  },
  computed: {
    keys() {
      if (this.mode === 'phone') {
        return ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'back']
      }
      if (this.allowDecimal) {
        return ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'back']
      }
      return ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'ok', '0', 'back']
    },
    displayValue() {
      if (this.mode === 'phone') return this.formatPhone(this.value)
      return String(this.value ?? '')
    },
  },
  methods: {
    digitsOnly(value) {
      return String(value || '').replace(/\D/g, '')
    },
    formatPhone(value) {
      const digits = this.digitsOnly(value).slice(0, 11)
      if (!digits) return ''
      let out = digits[0] || ''
      if (digits.length > 1) out += ` (${digits.slice(1, 4)}`
      if (digits.length >= 4) out += ')'
      if (digits.length > 4) out += ` ${digits.slice(4, 7)}`
      if (digits.length > 7) out += `-${digits.slice(7, 9)}`
      if (digits.length > 9) out += `-${digits.slice(9, 11)}`
      return out
    },
    emitValue(next) {
      this.$emit('input', next)
    },
    clear() {
      this.emitValue('')
    },
    done() {
      this.$emit('close')
    },
    press(key) {
      if (key === '' || key == null) return
      if (key === 'ok') {
        this.done()
        return
      }

      if (this.mode === 'phone') {
        const current = this.digitsOnly(this.value)
        if (key === 'back') {
          this.emitValue(current.slice(0, -1))
          return
        }
        if (current.length >= this.maxLength) return
        this.emitValue(`${current}${key}`)
        return
      }

      let current = String(this.value ?? '')
      if (key === 'back') {
        this.emitValue(current.slice(0, -1))
        return
      }

      if (key === '.') {
        if (!this.allowDecimal || current.includes('.')) return
        this.emitValue(current ? `${current}.` : '0.')
        return
      }

      if (!/^\d$/.test(key)) return
      if (current.replace('.', '').length >= this.maxLength) return
      if (current === '0' && !this.allowDecimal) {
        this.emitValue(key)
        return
      }
      if (current === '0' && this.allowDecimal) {
        this.emitValue(key)
        return
      }
      this.emitValue(`${current}${key}`)
    },
  },
}
</script>

<style lang="scss" scoped>
.num-pad {
  position: fixed;
  inset: 0;
  z-index: 2400;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.num-pad__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
}

.num-pad__panel {
  position: relative;
  z-index: 1;
  width: min(420px, 100%);
  padding: 16px 16px 20px;
  border-radius: 22px 22px 0 0;
  background: #eef2f7;
  border: 1px solid #d7e2ee;
  box-shadow: 0 -12px 40px rgba(18, 32, 51, 0.2);
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
}

.num-pad__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;

  strong {
    font-size: 16px;
    font-weight: 750;
    color: #122033;
  }
}

.num-pad__done {
  min-height: 40px;
  padding: 0 14px;
  border-radius: 12px;
  border: 0;
  background: #0b4f8a;
  color: #fff;
  font-weight: 750;
  cursor: pointer;
}

.num-pad__value {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 56px;
  margin-bottom: 14px;
  padding: 0 16px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #d7e2ee;
  font-size: 28px;
  font-weight: 800;
  color: #122033;
  letter-spacing: 0.02em;

  span {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.is-placeholder {
      color: #9aabbc;
      font-weight: 650;
    }
  }
}

.num-pad__clear {
  border: 0;
  background: transparent;
  color: #8aa0b5;
  cursor: pointer;
  font-size: 22px;
}

.num-pad__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.num-pad__key {
  height: 58px;
  border-radius: 16px;
  border: 1px solid #d7e2ee;
  background: #fff;
  color: #122033;
  font-size: 24px;
  font-weight: 750;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:active:not(:disabled) {
    background: #dceaf5;
    border-color: #0b4f8a;
  }

  &:disabled {
    opacity: 0;
    pointer-events: none;
  }

  &--action {
    color: #0b4f8a;
  }

  &--ok {
    background: #0b4f8a;
    border-color: #0b4f8a;
    color: #fff;
    font-size: 16px;
    letter-spacing: 0.04em;
  }

  &--wide {
    grid-column: span 1;
  }
}

@media (min-width: 640px) {
  .num-pad {
    align-items: center;
  }

  .num-pad__panel {
    border-radius: 20px;
    box-shadow: 0 18px 50px rgba(18, 32, 51, 0.22);
  }
}
</style>
